import { AUTOMATION_CONTEXT_OUTCOME, AUTOMATION_CONTEXT_STEP } from './helper';
import {
    AUTOMATION_EVENT_TIME_BASED,
} from './automationEvents';

/**
 * Build POST body for RecruitmentAutomationsEditor.
 *
 * Conditions are grouped: a top-level operator joins a list of groups, and each group has its own
 * operator joining its leaf conditions — e.g. `(A OR B) AND C` (RND-1378). The emitted `plan` is the
 * Advanced-Search format `{op, items}` the backend already evaluates.
 *
 * :param category: AUTOMATION_CONTEXT_STEP | AUTOMATION_CONTEXT_OUTCOME
 * :param contextId: step id string or numeric outcome status id
 * :param event: automation event id string
 * :param cron: optional cron patterns when event is time_based_event
 * :param groups: list of { op, conditions: [{ filter, value }] }
 * :param topOperator: 'and' | 'or' joining the groups
 * :param conditions/conditionOperator: legacy flat shape; used only when `groups` is absent
 * :param actionId: automation action id
 * :param actionValue: action configuration object
 */
export function buildRecruitmentAutomationPostPayload({
    category,
    contextId,
    event,
    cron,
    groups,
    topOperator,
    conditions,
    conditionOperator,
    actionId,
    actionValue,
}) {
    // Back-compat: if no grouped state is supplied, wrap the flat conditions in a single group.
    const safeGroups = Array.isArray(groups)
        ? groups
        : [{ op: conditionOperator, conditions: Array.isArray(conditions) ? conditions : [] }];
    const planOperator = topOperator || conditionOperator;

    let filterId = 1;
    const values = {};
    const items = [];
    for (const group of safeGroups) {
        const groupConditions = Array.isArray(group?.conditions) ? group.conditions : [];
        if (groupConditions.length === 0) {
            continue; // drop empty groups
        }
        const groupItems = [];
        for (const condition of groupConditions) {
            values[filterId] = condition.value;
            groupItems.push({ filter: condition.filter, id: filterId });
            filterId += 1; // ids must be globally unique across all groups
        }
        items.push({ op: group.op || planOperator, items: groupItems });
    }

    const plan = { op: planOperator, items };

    const payload = {
        values,
        plan,
        action: { id: actionId, value: actionValue && typeof actionValue === 'object' ? { ...actionValue } : {} },
        event,
    };

    if (category === AUTOMATION_CONTEXT_OUTCOME) {
        payload.outcome = Number(contextId);
    } else {
        payload.applicant_step = String(contextId);
    }

    if (event === AUTOMATION_EVENT_TIME_BASED && Array.isArray(cron) && cron.length > 0) {
        payload.cron = cron;
    }

    return payload;
}
